import React, { useEffect, useState } from 'react';
import { adminService } from '../services/adminService';
import { Button, Table, Input, Modal, Form, DatePicker, Checkbox, message, Popconfirm } from 'antd';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;


const Announcements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (dateRange.length === 2) {
        params.startDate = dateRange[0].format('YYYY-MM-DD');
        params.endDate = dateRange[1].format('YYYY-MM-DD');
      }
      const res = await adminService.getAnnouncements(params);
      setAnnouncements(res.announcements || []);
    } catch (err) {
      message.error('Failed to fetch announcements');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAnnouncements();
    // eslint-disable-next-line
  }, []);

  const openModal = (record = null) => {
    setEditing(record);
    setModalOpen(true);
    if (record) {
      form.setFieldsValue({
        ...record,
        start_date: record.start_date ? dayjs(record.start_date) : null,
        end_date: record.end_date ? dayjs(record.end_date) : null,
      });
    } else {
      form.resetFields();
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      // Convert dayjs objects to strings
      if (values.start_date) values.start_date = values.start_date.format('YYYY-MM-DD');
      if (values.end_date) values.end_date = values.end_date.format('YYYY-MM-DD');
      if (editing) {
        await adminService.updateAnnouncement(editing.id, values);
        message.success('Announcement updated');
      } else {
        await adminService.createAnnouncement(values);
        message.success('Announcement created');
      }
      setModalOpen(false);
      fetchAnnouncements();
    } catch (err) {
      message.error('Failed to save announcement');
    }
  };

  const handleDelete = async (id) => {
    try {
      await adminService.deleteAnnouncement(id);
      message.success('Announcement deleted');
      fetchAnnouncements();
    } catch (err) {
      message.error('Failed to delete announcement');
    }
  };

  const handleBulkDelete = async () => {
    try {
      await adminService.bulkDeleteAnnouncements(selectedRowKeys);
      message.success('Announcements deleted');
      setSelectedRowKeys([]);
      fetchAnnouncements();
    } catch (err) {
      message.error('Bulk delete failed');
    }
  };

  const columns = [
    { title: 'Title', dataIndex: 'title', key: 'title' },
    { title: 'Message', dataIndex: 'message', key: 'message' },
    { title: 'Active', dataIndex: 'active', key: 'active', render: v => v ? 'Yes' : 'No' },
    { title: 'Show Every Login', dataIndex: 'show_every_login', key: 'show_every_login', render: v => v ? 'Yes' : 'No' },
    { title: 'Start Date', dataIndex: 'start_date', key: 'start_date', render: v => v ? v.split('T')[0] : '' },
    { title: 'End Date', dataIndex: 'end_date', key: 'end_date', render: v => v ? v.split('T')[0] : '' },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <>
          <Button size="small" onClick={() => openModal(record)}>Edit</Button>
          <Popconfirm title="Delete this announcement?" onConfirm={() => handleDelete(record.id)}>
            <Button size="small" danger style={{ marginLeft: 8 }}>Delete</Button>
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <div>
      <h2>Announcements</h2>
      <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
        <Input.Search
          placeholder="Search title/message"
          value={search}
          onChange={e => setSearch(e.target.value)}
          onSearch={fetchAnnouncements}
          style={{ width: 200 }}
        />
        <RangePicker
          value={dateRange}
          onChange={setDateRange}
          style={{ width: 250 }}
        />
        <Button type="primary" onClick={() => openModal()}>Add Announcement</Button>
        {selectedRowKeys.length > 0 && (
          <Button danger onClick={handleBulkDelete}>Delete Selected</Button>
        )}
        <Button onClick={fetchAnnouncements}>Refresh</Button>
      </div>
      <Table
        rowKey="id"
        loading={loading}
        dataSource={announcements}
        columns={columns}
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
        }}
      />
      <Modal
        open={modalOpen}
        title={editing ? 'Edit Announcement' : 'Add Announcement'}
        onCancel={() => setModalOpen(false)}
        onOk={handleSave}
        okText="Save"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="message" label="Message" rules={[{ required: true }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="html_content" label="HTML Content">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="start_date" label="Start Date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="end_date" label="End Date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="show_every_login" valuePropName="checked">
            <Checkbox>Show Every Login</Checkbox>
          </Form.Item>
          <Form.Item name="active" valuePropName="checked">
            <Checkbox>Active</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Announcements;